import { Items } from "@pdfking/tfunc"
import { ordered_object } from "./ordered_object"

/**
 * Merges multiple objects into one.
 *
 * Later objects override earlier ones for duplicate keys.
 *
 * @param args - Objects to merge
 * @returns Merged object
 *
 * @example
 * ```ts
 * import { merge } from "@pdfking/rfunc"
 *
 * merge({ a: 1 }, { b: 2 }) // { a: 1, b: 2 }
 * merge({ a: 1, b: 2 }, { b: 3 }) // { a: 1, b: 3 }
 * merge() // {}
 * ```
 */
export function merge(): Record<string, never>
export function merge<A extends Record<string, unknown>>(a: A): A
export function merge<
  A extends Record<string, unknown>,
  B extends Record<string, unknown>,
>(a: A, b: B): A & B
export function merge<
  A extends Record<string, unknown>,
  B extends Record<string, unknown>,
  C extends Record<string, unknown>,
>(a: A, b: B, c: C): A & B & C
export function merge<
  A extends Record<string, unknown>,
  B extends Record<string, unknown>,
  C extends Record<string, unknown>,
  D extends Record<string, unknown>,
>(a: A, b: B, c: C, d: D): A & B & C & D
export function merge<T extends Record<string, unknown>>(
  ...args: T[]
): Record<string, unknown>
export function merge<T extends Record<string, unknown>>(
  ...args: T[]
): Record<string, unknown> {
  return ordered_object(
    args.reduce<[string, unknown][]>((a, b) => a.concat(Items(b)), [])
  )
}
